import { ScrollView, Text, View } from 'react-native';
import { useIsFocused, useRouter, type Href } from 'expo-router';
import { trpc } from '../../lib/trpc';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  LoadingSpinner,
  PageContainer,
  PageHeader,
  SectionHeader,
  Select,
  WebHead,
} from '../../components';

const WITHDRAWAL_STRATEGY_OPTIONS = [
  { value: 'blended_tax_optimized', label: 'Balanced (Recommended)' },
  { value: 'taxable_first', label: 'Use regular taxable money first' },
  { value: 'pretax_first', label: 'Use pre-tax money sooner' },
  { value: 'roth_preserve', label: 'Preserve Roth as long as possible' },
];

const ROTH_CONVERSION_OPTIONS = [
  { value: 'disabled', label: 'Keep taxes lower now' },
  { value: 'fill_to_combined_marginal_cap', label: 'Balanced (Recommended)' },
  { value: 'fill_gap_years_until_rmd', label: 'Reduce future taxes faster' },
];

const ACA_GUARDRAIL_OPTIONS = [
  { value: 'maximize_subsidies', label: 'Protect health insurance savings' },
  { value: 'balanced', label: 'Balanced (Recommended)' },
  { value: 'ignore_aca', label: 'Ignore for now' },
];

const IRMAA_GUARDRAIL_OPTIONS = [
  { value: 'avoid_irmaa', label: 'Avoid Medicare premium jumps' },
  { value: 'balanced', label: 'Balanced (Recommended)' },
  { value: 'ignore_irmaa', label: 'Ignore for now' },
];

const HSA_STRATEGY_OPTIONS = [
  { value: 'spend_current_medical', label: 'Use it for current medical costs' },
  { value: 'preserve_for_late_reimbursement', label: 'Save it for later (Recommended)' },
  { value: 'preserve_until_65_plus', label: 'Keep it untouched until 65+' },
];

const HANDLING_457B_OPTIONS = [
  { value: 'preserve_for_early_access', label: 'Keep it available for early retirement (Recommended)' },
  { value: 'roll_to_ira', label: 'Treat it more like other retirement money' },
];

const LEGACY_OPTIONS = [
  { value: 'neutral', label: 'No special preference (Recommended)' },
  { value: 'preserve_roth_for_heirs', label: 'Preserve Roth for later or heirs' },
  { value: 'spend_pretax_first_late', label: 'Use more pre-tax money later' },
];

const CASH_RESERVE_OPTIONS = [
  { value: 'twelve_months', label: 'Balanced cushion (12 months)' },
  { value: 'six_months', label: 'Lean cushion (6 months)' },
  { value: 'eighteen_months', label: 'Larger cushion (18 months)' },
  { value: 'disabled', label: 'Do not model a separate cushion' },
];

const DYNAMIC_SPENDING_OPTIONS = [
  { value: 'moderate', label: 'Moderate flexibility (Recommended)' },
  { value: 'flexible', label: 'More flexible in rough markets' },
  { value: 'disabled', label: 'Keep planned spending unchanged' },
];

function confidenceVariant(confidence: 'high' | 'medium' | 'low') {
  if (confidence === 'high') return 'green' as const;
  if (confidence === 'medium') return 'yellow' as const;
  return 'red' as const;
}

function getVisibleWithdrawalMode(value: string | null | undefined) {
  if (value === 'pretax_first' || value === 'roth_preserve' || value === 'taxable_first') {
    return value;
  }

  return 'blended_tax_optimized';
}

function getVisibleRothConversionMode(value: string | null | undefined) {
  if (value === 'disabled' || value === 'fill_to_combined_marginal_cap' || value === 'fill_gap_years_until_rmd') {
    return value;
  }

  return 'fill_to_combined_marginal_cap';
}

function getVisibleLegacyPreference(value: string | null | undefined) {
  if (value === 'preserve_roth_for_heirs' || value === 'spend_pretax_first_late') {
    return value;
  }

  return 'neutral';
}

function getVisibleCashReserveStrategy(value: string | null | undefined) {
  if (value === 'disabled' || value === 'six_months' || value === 'eighteen_months') {
    return value;
  }

  return 'twelve_months';
}

function getVisibleDynamicSpendingGuardrail(value: string | null | undefined) {
  if (value === 'disabled' || value === 'flexible') {
    return value;
  }

  return 'moderate';
}

function PreferenceHint({ children }: { children: string }) {
  return (
    <Text className="-mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
      {children}
    </Text>
  );
}

export default function TaxStrategyScreen() {
  const isFocused = useIsFocused();
  const router = useRouter();
  const utils = trpc.useUtils();
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const strategyQ = trpc.taxStrategy.get.useQuery(undefined, { enabled: isFocused });
  const recalcMutation = trpc.taxStrategy.recalculate.useQuery(undefined, { enabled: false });
  const updateConfigMutation = trpc.taxStrategy.updateConfig.useMutation({
    onSuccess: () => {
      void utils.taxStrategy.get.invalidate();
      void utils.fire.getPlan.invalidate();
      void utils.fire.getRecalcStatus.invalidate();
      void utils.fire.getLastRecalcImpact.invalidate();
    },
  });

  const data = strategyQ.data;
  const snapshot = data?.snapshot;
  const isDemo = householdQ.data?.isDemo ?? false;

  const patchConfig = async (patch: Record<string, unknown>) => {
    await updateConfigMutation.mutateAsync(patch);
  };

  if (strategyQ.isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas dark:bg-canvas-dark">
        <LoadingSpinner />
      </View>
    );
  }

  if (strategyQ.error || !data || !snapshot) {
    return (
      <ErrorState
        message={strategyQ.error?.message ?? 'Unable to load tax strategy.'}
        onRetry={() => strategyQ.refetch()}
      />
    );
  }

  return (
    <>
      <WebHead>
        <title>Tax Strategy – Per Diem</title>
      </WebHead>
      <ScrollView className="flex-1 bg-canvas dark:bg-canvas-dark">
        <PageContainer className="p-4" size="default">
          <PageHeader
            title="Tax Strategy"
            subtitle="How Per Diem models withdrawals, conversions, and tax guardrails for this household"
            closeTo="/(app)/fire-plan"
            action={!isDemo ? (
              <Button
                size="sm"
                variant="secondary"
                loading={strategyQ.isFetching || recalcMutation.isFetching}
                onPress={async () => {
                  await utils.taxStrategy.get.invalidate();
                  await recalcMutation.refetch();
                  await utils.taxStrategy.get.invalidate();
                }}
              >
                Refresh Strategy
              </Button>
            ) : undefined}
          />

          <Card className="mb-4">
            <View className="flex-row items-start justify-between gap-3">
              <View className="flex-1">
                <Text className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {snapshot.summary.title}
                </Text>
                <Text className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {snapshot.summary.body}
                </Text>
              </View>
              <Badge
                label={`${snapshot.confidence[0]!.toUpperCase()}${snapshot.confidence.slice(1)} confidence`}
                variant={confidenceVariant(snapshot.confidence)}
              />
            </View>

            <View className="mt-4 flex-row flex-wrap gap-2">
              <Badge
                label={snapshot.relevance.filingStatus === 'married_filing_jointly' ? 'MFJ' : 'Single / Unknown'}
                variant="gray"
              />
              {snapshot.relevance.state && <Badge label={snapshot.relevance.state} variant="blue" />}
              {snapshot.relevance.hasPretaxAccounts && <Badge label="Pre-tax accounts" variant="gray" />}
              {snapshot.relevance.hasTaxableBridgeAssets && <Badge label="Bridge assets" variant="gray" />}
              {snapshot.relevance.hasRothAccounts && <Badge label="Roth assets" variant="gray" />}
              {snapshot.relevance.hasGovernmental457b && <Badge label="457(b) detected" variant="gray" />}
              {snapshot.relevance.hasHsa && <Badge label="HSA detected" variant="gray" />}
              {snapshot.relevance.hasMortgage && <Badge label="Mortgage detected" variant="gray" />}
            </View>

            <Text className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Last recalculated {data.status.computedAt ? new Date(data.status.computedAt).toLocaleString() : 'not yet'}
            </Text>
          </Card>

          <Card className="mb-4">
            <SectionHeader title="Rules We Will Follow" />
            <View className="gap-3">
              {snapshot.activeRules.map((rule) => (
                <View key={rule.key} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                  <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {rule.title}
                  </Text>
                  <Text className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {rule.rationale}
                  </Text>
                  <Text className="mt-2 text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {rule.phase.replaceAll('_', ' ')}
                  </Text>
                </View>
              ))}
            </View>
          </Card>

          {snapshot.recommendedActions.length > 0 && (
            <Card className="mb-4">
              <SectionHeader title="What You May Need To Do" />
              <View className="gap-3">
                {snapshot.recommendedActions.map((action) => (
                  <View
                    key={action.key}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {action.title}
                    </Text>
                    <Text className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {action.body}
                    </Text>
                    {action.route && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 self-start"
                        onPress={() => router.push(action.route as Href)}
                      >
                        Open
                      </Button>
                    )}
                  </View>
                ))}
              </View>
            </Card>
          )}

          <Card className="mb-4">
            <SectionHeader title="Main Choices" />
            <Text className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              These are the main tax-strategy choices most households may reasonably want to adjust.
              Per Diem recommends balanced defaults for most people.
            </Text>
            <View className="gap-4">
              <Select
                label="How should Per Diem choose where spending comes from?"
                value={getVisibleWithdrawalMode(data.config.withdrawalStrategyMode)}
                options={WITHDRAWAL_STRATEGY_OPTIONS}
                onChange={(value) => { void patchConfig({ withdrawalStrategyMode: value }); }}
              />
              <PreferenceHint>
                This sets the order we use when estimating which accounts get tapped first in FIRE projections.
              </PreferenceHint>

              <Select
                label="How much cash cushion should retirement projections maintain?"
                value={getVisibleCashReserveStrategy(data.config.cashReserveStrategy)}
                options={CASH_RESERVE_OPTIONS}
                onChange={(value) => { void patchConfig({ cashReserveStrategy: value }); }}
              />
              <PreferenceHint>
                This is a household comfort preference. Per Diem preserves the cushion in normal years, uses it after rough market years, and refills it after stronger years.
              </PreferenceHint>

              <Select
                label="Should projections adjust flexible spending after bad market years?"
                value={getVisibleDynamicSpendingGuardrail(data.config.dynamicSpendingGuardrail)}
                options={DYNAMIC_SPENDING_OPTIONS}
                onChange={(value) => { void patchConfig({ dynamicSpendingGuardrail: value }); }}
              />
              <PreferenceHint>
                This only affects discretionary spending in Monte Carlo projections; fixed bills and planned life events stay intact.
              </PreferenceHint>

              {snapshot.relevance.hasPretaxAccounts && (
                <>
                  <Select
                    label="How aggressively should we plan future Roth conversions?"
                    value={getVisibleRothConversionMode(data.config.rothConversionMode)}
                    options={ROTH_CONVERSION_OPTIONS}
                    onChange={(value) => { void patchConfig({ rothConversionMode: value }); }}
                  />
                  <PreferenceHint>
                    Roth conversions mean intentionally moving some pre-tax retirement money into Roth during lower-income years.
                  </PreferenceHint>
                </>
              )}

              <Select
                label="How much should we protect ACA health insurance savings?"
                value={data.config.acaGuardrail ?? 'balanced'}
                options={ACA_GUARDRAIL_OPTIONS}
                onChange={(value) => { void patchConfig({ acaGuardrail: value }); }}
              />
              <PreferenceHint>
                Lower taxable income can sometimes protect marketplace health insurance discounts before Medicare age.
              </PreferenceHint>

              <Select
                label="How much should we avoid Medicare premium jumps later?"
                value={data.config.irmaaGuardrail ?? 'balanced'}
                options={IRMAA_GUARDRAIL_OPTIONS}
                onChange={(value) => { void patchConfig({ irmaaGuardrail: value }); }}
              />
              <PreferenceHint>
                Higher income later in life can increase Medicare premiums, so this choice controls how careful we should be.
              </PreferenceHint>

              {snapshot.relevance.hasHsa && (
                <>
                  <Select
                    label="How should we think about your HSA?"
                    value={data.config.hsaStrategy ?? 'preserve_for_late_reimbursement'}
                    options={HSA_STRATEGY_OPTIONS}
                    onChange={(value) => { void patchConfig({ hsaStrategy: value }); }}
                  />
                  <PreferenceHint>
                    HSAs can be spent now or saved for future medical costs, and many households benefit from keeping that flexibility.
                  </PreferenceHint>
                </>
              )}

              {snapshot.relevance.hasGovernmental457b && (
                <>
                  <Select
                    label="How should we treat your 457(b)?"
                    value={data.config.handling457b ?? 'preserve_for_early_access'}
                    options={HANDLING_457B_OPTIONS}
                    onChange={(value) => { void patchConfig({ handling457b: value }); }}
                  />
                  <PreferenceHint>
                    A 457(b) can be especially helpful for early retirement, so we surface that choice when we detect one.
                  </PreferenceHint>
                </>
              )}

              <Select
                label="What matters most for money you leave in Roth later on?"
                value={getVisibleLegacyPreference(data.config.legacyPreference)}
                options={LEGACY_OPTIONS}
                onChange={(value) => { void patchConfig({ legacyPreference: value }); }}
              />
              <PreferenceHint>
                This only matters if your plan leaves meaningful Roth money later in life and you want the strategy to lean one way.
              </PreferenceHint>
            </View>

            <View className="mt-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
              <Text className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                Current FIRE engine support
              </Text>
              <Text className="mt-1 text-sm text-amber-800 dark:text-amber-400">
                FIRE calculations apply these choices as planning rules, not tax-lot investment management:
                {' '}
                {snapshot.fireEngineSupport.implementedSettings.join(', ')}.
              </Text>
            </View>
          </Card>

          {snapshot.disabledRules.length > 0 && (
            <Card className="mb-4">
              <SectionHeader title="Not Relevant Right Now" />
              <View className="gap-3">
                {snapshot.disabledRules.map((rule) => (
                  <View key={rule.key}>
                    <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {rule.title}
                    </Text>
                    <Text className="text-sm text-gray-600 dark:text-gray-300">
                      {rule.reason}
                    </Text>
                  </View>
                ))}
              </View>
            </Card>
          )}

          {(snapshot.missingInputs.length > 0 || snapshot.unsupportedCases.length > 0) && (
            <Card className="mb-4">
              <SectionHeader title="Confidence Limits" />
              <View className="gap-3">
                {snapshot.missingInputs.map((item) => (
                  <View key={item.key}>
                    <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {item.label}
                    </Text>
                    <Text className="text-sm text-gray-600 dark:text-gray-300">
                      {item.reason}
                    </Text>
                  </View>
                ))}
                {snapshot.unsupportedCases.map((item) => (
                  <Text key={item} className="text-sm text-gray-600 dark:text-gray-300">
                    {item}
                  </Text>
                ))}
              </View>
            </Card>
          )}
        </PageContainer>
      </ScrollView>
    </>
  );
}
